/** A `bash` command still running in the sandbox after the agent moved on, as `job_started` announced it. */
export interface RunningJob {
  id: string;
  command: string;
  /** Epoch ms it started: the store's on attach, else when the client first heard of it. A re-announced job keeps its original. */
  startedAt: number;
}

/** A job report split into the sentence that says what happened and the output beneath it. */
export interface JobReportParts {
  /** "Background job j1 (`cargo clippy`) finished after 3m 2s", without the trailing colon. */
  headline: string;
  /** Everything after the first line, or empty when the report is one line. */
  output: string;
}

/**
 * Split a report the agent loop wrote for the model into what a reader scans and what they open.
 *
 * The format is the loop's, not a contract: the first line is a sentence and the rest is the
 * command's output. A report that breaks that shape still renders — as a headline with no body.
 */
export function splitJobReport(content: string): JobReportParts {
  const [first = '', ...rest] = content.split('\n');
  return { headline: first.replace(/:\s*$/, ''), output: rest.join('\n').trim() };
}

/** "12s", "4m 18s", "1h 3m" — the same shape the loop uses for "finished after …". */
export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
